/**
 * Clinical Evaluation Form (Unified)
 * ===================================
 * Unified form for IDE and MEDECIN to manage clinical evaluation.
 * - Dynamic filtering based on cancer type and treatment type
 * - Auto-calculated BMI
 * - Medical coherence rules
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Save, 
  AlertCircle,
  Loader2,
  Stethoscope,
  Pill,
  Activity,
  User,
  X
} from "lucide-react";
import TreatmentPlanEditor from "@/components/clinical/TreatmentPlanEditor";

// Mode d'administration (référentiel canonique backend clinical_config.TREATMENT_MODES, servi par /clinical/reference-data).
// Concept distinct de la classe thérapeutique du plan ; libellés d'affichage uniquement.
const TREATMENT_MODE_LABELS = { IV: "IV / HDJ", Oral: "Oral à domicile", Mixte: "Mixte IV + oral" };

// Règle métier fournie par le backend (/clinical/reference-data → metastaticStages, référentiel canonique clinical_config.STAGES).
const isMetastaticStageFor = (referenceData) => (stage) => Boolean(stage) && (referenceData?.metastaticStages || []).includes(stage);

// Valeur héritée absente du référentiel courant : on la garde affichable/sélectionnable, sans la remplacer.
function withLegacyOption(options, current) {
  const list = Array.isArray(options) ? options : [];
  return current && !list.includes(current) ? [current, ...list] : list;
}

// Erreurs 422 FastAPI : `detail` est un tableau [{loc, msg}] → texte lisible.
function formatApiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => `${(d.loc || []).filter((x) => x !== "body").join(".")}: ${d.msg}`).join(" ; ");
  }
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return detail || err?.message || fallback;
}

function inferTreatmentMode(plan, evaluation) {
  const explicit = evaluation?.treatmentType;
  if (Object.keys(TREATMENT_MODE_LABELS).includes(explicit)) return explicit;
  const oralCount = (plan?.oralTreatments || []).length;
  const routes = (plan?.mainProtocol?.components || [])
    .map((component) => (component.route || "").toString().toUpperCase())
    .filter(Boolean);
  const hasMainProtocol = Boolean(plan?.mainProtocol);
  const hasOralRoute = routes.some((route) => ["PO", "ORAL", "ORALE"].includes(route));
  const hasNonOralRoute = routes.some((route) => !["PO", "ORAL", "ORALE"].includes(route));
  if (oralCount > 0 && (hasMainProtocol || hasNonOralRoute)) return "Mixte";
  if (oralCount > 0 || (hasOralRoute && !hasNonOralRoute)) return "Oral";
  return "IV";
}

export default function ClinicalEvaluationForm() {
  const { patientId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [patient, setPatient] = useState(null);
  const [existingEvaluation, setExistingEvaluation] = useState(null);
  const [referenceData, setReferenceData] = useState(null);
  const isMetastaticStage = useMemo(() => isMetastaticStageFor(referenceData), [referenceData]);

  // SPRINT 2 — plan de traitement (source unique)
  const [plan, setPlan] = useState({
    mainProtocol: null,
    complementaryMeds: [],
    oralTreatments: [],
    therapeuticClasses: [],
    treatmentStatus: "En cours",
    height: "",
    weight: "",
  });
  const [planIsLegacyView, setPlanIsLegacyView] = useState(false);

  // Form state
  const [form, setForm] = useState({
    // Cancer identification
    cancerType: "",
    cancerTypeOther: "", // For "Autre"
    cancerSubtype: "",
    cancerSubtypeOther: "", // For "Non précisé" with details
    stage: "",
    diagnosisDate: "",
    biomarkers: [],
    metastaticSites: [],
    // Treatment
    therapeuticLine: "",
    treatmentType: "",
    molecules: [], // Selected molecules from DCI list
    administrationRoute: "",
    treatmentFrequency: "",
    treatmentStartDate: "",
    treatmentStatus: "En cours",
    // Patient profile
    ecogStatus: null,
    weight: "",
    height: ""
  });

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [patientRes, refRes, evalRes, planRes] = await Promise.all([
          api.get(`/patients/${patientId}`),
          api.get(`/clinical/reference-data`),
          api.get(`/clinical/patients/${patientId}/evaluation`).catch(() => ({ data: { exists: false } })),
          api.get(`/patients/${patientId}/treatment-plan`).catch(() => ({ data: { exists: false } })),
        ]);

        setPatient(patientRes.data);
        setReferenceData(refRes.data);

        let loadedPlan = null;

        // SPRINT 2 — charge le plan actif (réel ou virtuel legacy)
        if (planRes.data?.exists && planRes.data.plan) {
          const p = planRes.data.plan;
          loadedPlan = {
            mainProtocol: p.mainProtocol || null,
            complementaryMeds: p.complementaryMeds || [],
            oralTreatments: p.oralTreatments || [],
            therapeuticClasses: p.therapeuticClasses || [],
            treatmentStatus: p.treatmentStatus || "En cours",
            height: p.surfaceArea?.computedFrom?.height || "",
            weight: p.surfaceArea?.computedFrom?.weight || "",
          };
          setPlan(loadedPlan);
          setPlanIsLegacyView(p.isLegacyView === true);
        }

        // Pre-fill from existing evaluation or patient data
        if (evalRes.data.exists && evalRes.data.evaluation) {
          setExistingEvaluation(evalRes.data.evaluation);
          const eval_ = evalRes.data.evaluation;
          setForm({
            cancerType: eval_.cancerType || "",
            cancerSubtype: eval_.cancerSubtype || "",
            stage: eval_.stage || "",
            diagnosisDate: eval_.diagnosisDate || "",
            biomarkers: eval_.biomarkers || [],
            metastaticSites: eval_.metastaticSites || [],
            therapeuticLine: eval_.therapeuticLine || "",
            treatmentType: inferTreatmentMode(loadedPlan, eval_),
            molecules: eval_.molecules || [],
            administrationRoute: eval_.administrationRoute || "",
            treatmentFrequency: eval_.treatmentFrequency || "",
            treatmentStartDate: eval_.treatmentStartDate || "",
            treatmentStatus: eval_.treatmentStatus || "En cours",
            ecogStatus: eval_.ecogStatus,
            weight: eval_.weight || "",
            height: eval_.height || "",
            cancerTypeOther: eval_.cancerTypeOther || "",
            cancerSubtypeOther: eval_.cancerSubtypeOther || "",
          });
        } else {
          // Pre-fill from patient data if available
          const p = patientRes.data;
          setForm(prev => ({
            ...prev,
            cancerType: p.cancerType || "",
            cancerSubtype: p.cancerSubtype || p.cancerTypeDetail || "",
            treatmentType: inferTreatmentMode(loadedPlan, p),
            therapeuticLine: p.therapeuticLine || "",
            molecules: p.molecules || [],
            weight: p.weight || "",
            height: p.height || ""
          }));
        }
      } catch (err) {
        toast.error(formatApiError(err, "Erreur lors du chargement"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [patientId]);

  // Dynamic filtered data based on selections
  const filteredSubtypes = useMemo(() => {
    if (!referenceData?.allSubtypes || !form.cancerType) return [];
    return referenceData.allSubtypes[form.cancerType] || ["Non précisé"];
  }, [referenceData, form.cancerType]);

  const filteredBiomarkers = useMemo(() => {
    if (!referenceData?.allBiomarkers || !form.cancerType) return [];
    return referenceData.allBiomarkers[form.cancerType] || ["Inconnu"];
  }, [referenceData, form.cancerType]);

  // Calculate BMI
  const bmi = useMemo(() => {
    if (form.weight && form.height) {
      const w = parseFloat(form.weight);
      const h = parseFloat(form.height) / 100; // cm to m
      if (w > 0 && h > 0) {
        return (w / (h * h)).toFixed(1);
      }
    }
    return null;
  }, [form.weight, form.height]);

  // Handle form changes with auto-reset of dependent fields
  const handleChange = useCallback((field, value) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      
      // Reset dependent fields when cancer type changes
      if (field === "cancerType") {
        updated.cancerSubtype = "";
        updated.biomarkers = [];
        updated.molecules = [];
      }
      
      // Reset molecules when treatment type changes
      if (field === "treatmentType") {
        updated.molecules = [];
      }
      
      // Clear metastatic sites if not metastatic stage (aligné sur la règle backend)
      if (field === "stage" && !isMetastaticStage(value)) {
        updated.metastaticSites = [];
      }
      
      return updated;
    });
  }, [isMetastaticStage]);

  // Toggle molecule selection
  const toggleMolecule = useCallback((molecule) => {
    setForm(prev => {
      const newMolecules = prev.molecules.includes(molecule)
        ? prev.molecules.filter(m => m !== molecule)
        : [...prev.molecules, molecule];
      return { ...prev, molecules: newMolecules };
    });
  }, []);

  // Toggle biomarker selection
  const toggleBiomarker = useCallback((biomarker) => {
    setForm(prev => {
      const newBiomarkers = prev.biomarkers.includes(biomarker)
        ? prev.biomarkers.filter(b => b !== biomarker)
        : [...prev.biomarkers, biomarker];
      return { ...prev, biomarkers: newBiomarkers };
    });
  }, []);

  // Toggle metastatic site selection
  const toggleMetastaticSite = useCallback((site) => {
    setForm(prev => {
      const newSites = prev.metastaticSites.includes(site)
        ? prev.metastaticSites.filter(s => s !== site)
        : [...prev.metastaticSites, site];
      return { ...prev, metastaticSites: newSites };
    });
  }, []);

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.cancerType) {
      toast.error("Veuillez sélectionner le type de cancer");
      return;
    }
    if (!form.stage) {
      toast.error("Veuillez sélectionner le stade");
      return;
    }
    if (!form.treatmentType) {
      toast.error("Veuillez sélectionner le mode de traitement");
      return;
    }

    setSaving(true);
    try {
      // SPRINT 2 — Les champs de traitement viennent du plan (source unique)
      const mp = plan.mainProtocol || {};
      const derivedMolecules = (mp.components || []).map((c) => c.dci).filter(Boolean);
      const derivedRoute = mp.administrationRoute || (mp.components || [])[0]?.route || null;
      const derivedFrequency = mp.cycle?.frequency || null;
      const derivedTherapeuticLine = mp.therapeuticLine || form.therapeuticLine || null;

      const payload = {
        ...form,
        // Dérivés du plan pour backward-compat des consommateurs legacy
        molecules: derivedMolecules,
        administrationRoute: derivedRoute,
        treatmentFrequency: derivedFrequency,
        therapeuticLine: derivedTherapeuticLine,
        treatmentStatus: plan.treatmentStatus || form.treatmentStatus,
        treatmentStartDate: mp.startedAt || form.treatmentStartDate,
        weight: plan.weight ? parseFloat(plan.weight) : (form.weight ? parseFloat(form.weight) : null),
        height: plan.height ? parseFloat(plan.height) : (form.height ? parseFloat(form.height) : null),
        ecogStatus: form.ecogStatus !== null && form.ecogStatus !== "" ? parseInt(form.ecogStatus) : null
      };

      // SPRINT 2 F3 — Ordre inversé + erreur isolée :
      // 1. PUT /treatment-plan EN PREMIER (source de vérité). Si ça échoue,
      //    rien n'est écrit côté serveur et l'utilisateur est bloqué proprement.
      // 2. PUT/POST /clinical/evaluation ensuite (contexte clinique). Si ça
      //    échoue, le plan est déjà persisté (aucune perte critique) — on
      //    émet un avertissement et on reste sur le formulaire pour réessayer.
      const hasPlanPayload = plan.mainProtocol || (plan.complementaryMeds || []).length > 0 || (plan.oralTreatments || []).length > 0;
      if (hasPlanPayload) {
        const cleanMain = plan.mainProtocol
          ? Object.fromEntries(Object.entries(plan.mainProtocol).filter(([k]) => !k.startsWith("_")))
          : null;
        try {
          await api.put(`/patients/${patientId}/treatment-plan`, {
            mainProtocol: cleanMain,
            complementaryMeds: plan.complementaryMeds || [],
            oralTreatments: plan.oralTreatments || [],
            therapeuticClasses: plan.therapeuticClasses || [],
            treatmentStatus: plan.treatmentStatus || "En cours",
            height: plan.height ? parseFloat(plan.height) : null,
            weight: plan.weight ? parseFloat(plan.weight) : null,
          });
        } catch (err) {
          const message = formatApiError(err, "Erreur sauvegarde du plan de traitement");
          toast.error(`Échec de la sauvegarde du plan : ${message}`);
          setSaving(false);
          return; // Abort — rien n'a été écrit, l'utilisateur peut corriger
        }
      }

      // Étape 2 — contexte clinique (non bloquant si échec)
      let clinicalOk = true;
      try {
        if (existingEvaluation) {
          await api.put(`/clinical/patients/${patientId}/evaluation`, payload);
        } else {
          await api.post(`/clinical/patients/${patientId}/evaluation`, payload);
        }
      } catch (err) {
        clinicalOk = false;
        const message = formatApiError(err, "Erreur sauvegarde du contexte clinique");
        if (hasPlanPayload) {
          toast.warning(
            `Plan de traitement enregistré, mais contexte clinique non mis à jour (${message}). Réessayez la sauvegarde.`
          );
        } else {
          toast.error(`Échec de la sauvegarde : ${message}`);
        }
      }

      if (clinicalOk) {
        toast.success(existingEvaluation
          ? "Évaluation mise à jour avec succès"
          : "Évaluation clinique créée - Dossier activé"
        );
        navigate(-1);
      } else {
        // On reste sur le formulaire pour permettre une retentative du contexte clinique
        setSaving(false);
      }
    } catch (err) {
      toast.error(formatApiError(err, "Erreur lors de la sauvegarde"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!patient || !referenceData) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>Impossible de charger les données</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate(-1)}
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-slate-900">
            {existingEvaluation ? "Modifier l'évaluation clinique" : "Évaluation clinique"}
          </h1>
          <p className="text-sm text-slate-500">
            {patient.firstName} {patient.lastName} • {patient.phone}
          </p>
        </div>
        {existingEvaluation && (
          <Badge variant="outline" className="ml-auto">
            Version {existingEvaluation.version}
          </Badge>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section 1: Cancer Identification */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Stethoscope className="w-5 h-5 text-emerald-600" />
              Identification du cancer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Cancer Type */}
              <div>
                <Label>Type de cancer *</Label>
                <Select value={form.cancerType} onValueChange={(v) => handleChange("cancerType", v)}>
                  <SelectTrigger data-testid="cancer-type-select">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {withLegacyOption(referenceData.cancerTypes, form.cancerType).map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Champ libre si "Autre" */}
                {form.cancerType === "Autre" && (
                  <Input
                    className="mt-2"
                    placeholder="Précisez le type de cancer..."
                    value={form.cancerTypeOther}
                    onChange={(e) => handleChange("cancerTypeOther", e.target.value)}
                    data-testid="cancer-type-other"
                  />
                )}
              </div>

              {/* Cancer Subtype - Dynamic */}
              <div>
                <Label>Sous-type / Cohorte</Label>
                <Select 
                  value={form.cancerSubtype} 
                  onValueChange={(v) => handleChange("cancerSubtype", v)}
                  disabled={!form.cancerType}
                >
                  <SelectTrigger data-testid="cancer-subtype-select">
                    <SelectValue placeholder={form.cancerType ? "Sélectionner..." : "Choisir d'abord le type"} />
                  </SelectTrigger>
                  <SelectContent>
                    {withLegacyOption(filteredSubtypes, form.cancerSubtype).map(subtype => (
                      <SelectItem key={subtype} value={subtype}>{subtype}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Champ libre si "Non précisé" */}
                {form.cancerSubtype === "Non précisé" && (
                  <Input
                    className="mt-2"
                    placeholder="Précisez le sous-type..."
                    value={form.cancerSubtypeOther}
                    onChange={(e) => handleChange("cancerSubtypeOther", e.target.value)}
                    data-testid="cancer-subtype-other"
                  />
                )}
              </div>

              {/* Stage */}
              <div>
                <Label>Stade *</Label>
                <Select value={form.stage} onValueChange={(v) => handleChange("stage", v)}>
                  <SelectTrigger data-testid="stage-select">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {withLegacyOption(referenceData.stages, form.stage).map(stage => (
                      <SelectItem key={stage} value={stage}>{stage}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Diagnosis Date */}
              <div>
                <Label>Date du diagnostic</Label>
                <Input
                  type="date"
                  value={form.diagnosisDate}
                  onChange={(e) => handleChange("diagnosisDate", e.target.value)}
                  data-testid="diagnosis-date-input"
                />
              </div>
            </div>

            {/* Biomarkers - Dynamic filtered */}
            {form.cancerType && filteredBiomarkers.length > 0 && (
              <div>
                <Label>Biomarqueurs (sélection multiple)</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {filteredBiomarkers.map(biomarker => (
                    <Badge
                      key={biomarker}
                      variant={form.biomarkers.includes(biomarker) ? "default" : "outline"}
                      className={`cursor-pointer transition-colors ${
                        form.biomarkers.includes(biomarker) 
                          ? "bg-emerald-600 hover:bg-emerald-700" 
                          : "hover:bg-emerald-50"
                      }`}
                      onClick={() => toggleBiomarker(biomarker)}
                      data-testid={`biomarker-${biomarker}`}
                    >
                      {biomarker}
                      {form.biomarkers.includes(biomarker) && (
                        <X className="w-3 h-3 ml-1" />
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Metastatic Sites - stades métastatiques uniquement (règle backend) */}
            {isMetastaticStage(form.stage) && (
              <div>
                <Label>Sites métastatiques</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {referenceData.metastaticSites?.map(site => (
                    <Badge
                      key={site}
                      variant={form.metastaticSites.includes(site) ? "default" : "outline"}
                      className={`cursor-pointer transition-colors ${
                        form.metastaticSites.includes(site) 
                          ? "bg-red-600 hover:bg-red-700" 
                          : "hover:bg-red-50"
                      }`}
                      onClick={() => toggleMetastaticSite(site)}
                      data-testid={`metastatic-site-${site}`}
                    >
                      {site}
                      {form.metastaticSites.includes(site) && (
                        <X className="w-3 h-3 ml-1" />
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 2: Treatment — SPRINT 2 source unique (patientTreatmentPlan) */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Pill className="w-5 h-5 text-blue-600" />
              Plan de traitement
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Mode de traitement *</Label>
                <Select value={form.treatmentType} onValueChange={(v) => handleChange("treatmentType", v)}>
                  <SelectTrigger data-testid="treatment-mode-select">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(referenceData.treatmentModes || Object.keys(TREATMENT_MODE_LABELS)).map(mode => (
                      <SelectItem key={mode} value={mode} data-testid={`treatment-mode-option-${mode.toLowerCase()}`}>{TREATMENT_MODE_LABELS[mode] || mode}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Source unique : éditeur de plan */}
            <TreatmentPlanEditor
              value={plan}
              onChange={setPlan}
              cancerType={form.cancerType}
              treatmentType={form.treatmentType}
              treatmentStatuses={referenceData.treatmentStatus || []}
              therapeuticLines={referenceData.therapeuticLines || []}
              moleculesSuggestions={referenceData.molecules || []}
              isLegacyView={planIsLegacyView}
              treatmentMode={form.treatmentType}
            />
          </CardContent>
        </Card>

        {/* Section 3: Patient Profile */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="w-5 h-5 text-purple-600" />
              Profil patient
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Poids, taille et IMC sont gérés dans le plan de traitement (ci-dessus) —
              affichés ici en lecture seule pour référence.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* ECOG */}
              <div>
                <Label>ECOG</Label>
                <Select 
                  value={form.ecogStatus !== null ? String(form.ecogStatus) : ""} 
                  onValueChange={(v) => handleChange("ecogStatus", v)}
                >
                  <SelectTrigger data-testid="ecog-select">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    {referenceData.ecogStatus?.map(status => (
                      <SelectItem key={status} value={String(status)}>
                        {status} - {
                          status === 0 ? "Activité normale" :
                          status === 1 ? "Restriction légère" :
                          status === 2 ? "Alité < 50%" :
                          status === 3 ? "Alité > 50%" :
                          "Grabataire"
                        }
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Weight / Height / BMI — SPRINT 2 : gérés dans le plan (SC Mosteller).
                  Gardés visibles ici en lecture seule pour continuité visuelle. */}
              <div>
                <Label>Poids (kg)</Label>
                <div className="h-10 flex items-center px-3 rounded-md border bg-slate-50 border-slate-200 text-slate-600 text-sm" data-testid="profile-weight-readonly">
                  {plan.weight || "—"}
                </div>
              </div>
              <div>
                <Label>Taille (cm)</Label>
                <div className="h-10 flex items-center px-3 rounded-md border bg-slate-50 border-slate-200 text-slate-600 text-sm" data-testid="profile-height-readonly">
                  {plan.height || "—"}
                </div>
              </div>
              {/* BMI - Auto calculated from plan values */}
              <div>
                <Label>IMC (calculé)</Label>
                <div className={`h-10 flex items-center px-3 rounded-md border ${
                  (() => {
                    const w = parseFloat(plan.weight);
                    const h = parseFloat(plan.height) / 100;
                    const b = (w > 0 && h > 0) ? (w / (h * h)) : null;
                    if (b == null) return "bg-slate-50 border-slate-200 text-slate-400";
                    return b < 18.5 ? "bg-yellow-50 border-yellow-200 text-yellow-700"
                      : b >= 30 ? "bg-red-50 border-red-200 text-red-700"
                      : "bg-green-50 border-green-200 text-green-700";
                  })()
                }`}>
                  {(() => {
                    const w = parseFloat(plan.weight);
                    const h = parseFloat(plan.height) / 100;
                    return (w > 0 && h > 0) ? <span className="font-medium">{(w/(h*h)).toFixed(1)} kg/m²</span> : <span>—</span>;
                  })()}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end gap-3">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate(-1)}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button 
            type="submit" 
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="submit-evaluation-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {existingEvaluation ? "Mettre à jour" : "Enregistrer"}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}


function MoleculeMultiAutocomplete({ selectedMolecules, onAdd, molecules, testId }) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const inputRef = useRef(null);

  const filtered = useMemo(() => {
    if (!search || search.length < 2) return [];
    const q = search.toLowerCase();
    return molecules
      .filter(m => m.toLowerCase().includes(q) && !selectedMolecules.includes(m))
      .slice(0, 12);
  }, [search, molecules, selectedMolecules]);

  const handleSelect = (mol) => {
    onAdd(mol);
    setSearch("");
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleInputChange = (e) => {
    const v = e.target.value;
    setSearch(v);
    setIsOpen(v.length >= 2);
    setHighlighted(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || filtered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted(h => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlighted >= 0) {
      e.preventDefault();
      handleSelect(filtered[highlighted]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={search}
        onChange={handleInputChange}
        onFocus={() => search.length >= 2 && setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        onKeyDown={handleKeyDown}
        placeholder="Rechercher une molécule (DCI)..."
        className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
        data-testid={testId}
        autoComplete="off"
      />
      {isOpen && filtered.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtered.map((mol, i) => (
            <button
              key={mol}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(mol); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 ${
                i === highlighted ? "bg-emerald-50 text-emerald-700" : "text-slate-700"
              }`}
              data-testid={`molecule-option-${mol.replace(/\s/g, '-')}`}
            >
              {mol}
            </button>
          ))}
        </div>
      )}
      {search.length >= 2 && filtered.length === 0 && isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs text-slate-400">
          Aucune molécule trouvée pour "{search}"
        </div>
      )}
    </div>
  );
}
